import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import GroupMembersHeader from '@web/src/features/group/group-members-header/group-members-header'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { GroupJoinRequestInfo, ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CheckIcon, ClockIcon, UserSearchIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ApproveJoinRequestMutation,
  ApproveJoinRequestMutationVariables,
  GroupJoinRequestSortEnum,
  GroupJoinRequestStatus,
  GroupMembersPageGroupQuery,
  GroupMembersRequestPageQuery,
  GroupMembersRequestPageQueryVariables,
} from 'types/graphql'
import { z } from 'zod'
import { GROUP_QUERY } from './$groupIdOrSlug_.members'

dayjs.extend(relativeTime)

export const QUERY: TypedDocumentNode<GroupMembersRequestPageQuery, GroupMembersRequestPageQueryVariables> = gql`
  query GroupMembersRequestPageQuery($page: Int, $filter: GroupJoinRequestsFilter!, $sortBy: GroupJoinRequestSortEnum) {
    groupJoinRequestsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        ...GroupJoinRequestInfo
      }
      count
      perPage
    }
  }
  ${GroupJoinRequestInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const APPROVE_JOIN_REQUEST: TypedDocumentNode<ApproveJoinRequestMutation, ApproveJoinRequestMutationVariables> = gql`
  mutation ApproveJoinRequestMutation($id: String!, $approve: Boolean!) {
    approveJoinRequest(id: $id, approve: $approve) {
      ...GroupJoinRequestInfo
    }
  }
  ${GroupJoinRequestInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

type GroupJoinRequestsSearchInput = {
  page?: number
  name?: string
  status?: string
  sortBy?: string
}

type GroupJoinRequestsLoaderDeps = {
  page: number
  name?: string
  status: string
  sortBy: string
}

function resolveGroupJoinRequestsSearchParams(search: GroupJoinRequestsSearchInput): GroupJoinRequestsLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    status: search.status ?? 'PENDING',
    sortBy: search.sortBy ?? 'NEWEST',
  }
}

function groupJoinRequestsQueryVariables(
  groupIdOrSlug: string,
  deps: GroupJoinRequestsLoaderDeps
): GroupMembersRequestPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      groupId: groupIdOrSlug,
      name: deps.name,
      status: deps.status === 'ALL' ? undefined : (deps.status as GroupJoinRequestStatus),
    },
    sortBy: deps.sortBy as GroupJoinRequestSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/group/$groupIdOrSlug_/join-requests')({
  component: GroupMembersRequestPageComponent,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    status: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        status: 'PENDING',
        sortBy: 'NEWEST',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Join Requests | ${env.VITE_APP_NAME}`,
      description: 'Browse a list of group members.',
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    }),
  loaderDeps: ({ search }) => resolveGroupJoinRequestsSearchParams(search),
  loader: async ({ context, params, deps }) => {
    const resolvedDeps = deps as GroupJoinRequestsLoaderDeps
    const variables = groupJoinRequestsQueryVariables(params.groupIdOrSlug, resolvedDeps)

    const [{ queryRef }, groupPreload] = await Promise.all([
      queryAndPreload(context, QUERY, { variables }),
      queryAndPreload(context, GROUP_QUERY, {
        variables: { groupIdOrSlug: params.groupIdOrSlug },
      }),
    ])
    return {
      queryRef,
      groupQueryRef: groupPreload.queryRef,
      groupQueryData: groupPreload.queryData,
    }
  },
})

function GroupMembersRequestPageComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const formRef = useRef(null)
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveGroupJoinRequestsSearchParams(search), [search])
  const client = useApolloClient()
  const loaderData = Route.useLoaderData()
  const { queryRef, groupQueryRef } = loaderData

  // State for reject confirmation modal
  const [rejectConfirmModal, setRejectConfirmModal] = useState(false)
  const [requestToReject, setRequestToReject] = useState<{ id: string; profileName: string } | null>(null)

  const { data, error } = useReadQuery(queryRef as QueryRef<GroupMembersRequestPageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formDefaultValues = useMemo(
    () => ({
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      status: resolvedSearch.status,
      sortBy: resolvedSearch.sortBy,
    }),
    [resolvedSearch.page, resolvedSearch.name, resolvedSearch.status, resolvedSearch.sortBy]
  )

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })

  const { data: groupData } = useReadQuery(groupQueryRef as QueryRef<GroupMembersPageGroupQuery>)
  const resolvedGroupQuery = groupData ?? loaderData.groupQueryData

  const [approveJoinRequest, { loading: respondLoading }] = useMutation(APPROVE_JOIN_REQUEST, {
    onCompleted: async (mutationData) => {
      const approved = mutationData.approveJoinRequest.status === 'APPROVED'
      toast.success(approved ? t('Join request approved successfully') : t('Join request rejected successfully'))
      // Refetch group data and reload the page to refresh join requests
      await client.refetchQueries({ include: [GROUP_QUERY] })
      navigate({ search, replace: true })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const handleLiveFilter = useCallback(() => {
    const curPage = formMethods.getValues('page')
    const name = formMethods.getValues('name')
    const status = formMethods.getValues('status') as GroupJoinRequestStatus
    const sortBy = formMethods.getValues('sortBy') as GroupJoinRequestSortEnum

    // Navigate to the same page with new search parameters
    navigate({
      search: {
        ...search,
        page: curPage,
        name: name && name.length > 0 ? name : undefined,
        sortBy: sortBy,
        status: status || undefined,
      },
      replace: true,
      viewTransition: false,
    })
  }, [formMethods, navigate, search])

  const handleResetFilters = () => {
    formMethods.reset({
      page: 1,
      name: '',
      status: null,
      sortBy: 'NEWEST',
    })
    handleLiveFilter()
  }

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      formMethods.setValue('page', 1)
      formMethods.setValue('name', value)
      handleLiveFilter()
    },
    [formMethods, handleLiveFilter]
  )

  const handleResponse = (requestId: string, approve: boolean) => {
    approveJoinRequest({
      variables: {
        id: requestId,
        approve,
      },
    })
  }

  const handleRejectClick = (requestId: string, profileName: string) => {
    setRequestToReject({ id: requestId, profileName })
    setRejectConfirmModal(true)
  }

  const confirmRejectRequest = () => {
    if (requestToReject) {
      handleResponse(requestToReject.id, false)
      setRejectConfirmModal(false)
      setRequestToReject(null)
    }
  }

  const pageData = useMemo(
    () => data?.groupJoinRequestsPage || { entries: [], count: 0, perPage: 24 },
    [data?.groupJoinRequestsPage]
  )
  const currentPage = useWatch({ control: formMethods.control, name: 'page' })
  const watchName = useWatch({ control: formMethods.control, name: 'name' })

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <GroupMembersHeader activeTab={'requests'} group={resolvedGroupQuery?.group} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Search members') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Search members by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-3 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-4">
                      <NativeSelectFormField label={t('Status')} name="status" onValueChange={handleLiveFilter}>
                        <option value="ALL">
                          {t('All Requests ({{count}})', {
                            count: resolvedGroupQuery?.group?.joinRequestCounts?.totalCount || 0,
                          })}
                        </option>
                        <option value="PENDING">
                          {t('Pending ({{count}})', {
                            count: resolvedGroupQuery?.group?.joinRequestCounts?.pendingCount || 0,
                          })}
                        </option>
                        <option value="APPROVED">
                          {t('Approved ({{count}})', {
                            count: resolvedGroupQuery?.group?.joinRequestCounts?.approvedCount || 0,
                          })}
                        </option>
                        <option value="REJECTED">
                          {t('Rejected ({{count}})', {
                            count: resolvedGroupQuery?.group?.joinRequestCounts?.rejectedCount || 0,
                          })}
                        </option>
                      </NativeSelectFormField>

                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                        <option value="NAME">{t('Name')}</option>
                        <option value="STATUS">{t('Status')}</option>
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {/* Results */}
            {pageData.entries.length > 0 && (
              <div className="flex w-full flex-col gap-4">
                {pageData.entries.map((request) => (
                  <Card
                    className="w-full cursor-pointer overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1"
                    key={request.id}
                    size="sm"
                  >
                    <CardContent className="px-3 py-1">
                      <div className="flex w-full flex-col items-start gap-3">
                        <div className="flex w-full items-center justify-between gap-2">
                          <SFProfileBanner
                            className="cursor-pointer"
                            lowerTitle={
                              <div className="flex flex-col items-start gap-1">
                                <Badge
                                  className={cn(
                                    'gap-1 rounded-sm px-2 shadow-sm',
                                    request.status === 'PENDING' && 'bg-amber-500 text-black',
                                    request.status === 'APPROVED' && 'bg-green-600 text-black',
                                    request.status === 'REJECTED' && 'bg-primary text-primary-foreground'
                                  )}
                                  size="sm"
                                  variant="secondary"
                                >
                                  {request.status === 'PENDING' ? (
                                    <ClockIcon />
                                  ) : request.status === 'APPROVED' ? (
                                    <CheckIcon />
                                  ) : (
                                    <XIcon />
                                  )}
                                  {t(request.status)}
                                </Badge>
                                <p className="text-muted-foreground text-xs">
                                  {t('Requested: {{time}}', { time: dayjs(request.createdAt).fromNow() })}
                                </p>
                                {request.respondedAt && request.status !== 'PENDING' && (
                                  <p className="text-muted-foreground text-xs">
                                    {t('Responded: {{time}}', { time: dayjs(request.respondedAt).fromNow() })}
                                    {request.respondedBy && t(' by {{name}}', { name: request.respondedBy.name })}
                                  </p>
                                )}
                              </div>
                            }
                            onClick={() =>
                              navigate({
                                to: '/profile/$profileId/about',
                                params: { profileId: request.profile.id },
                              } as never)
                            }
                            profile={request.profile}
                            size="xl"
                          />

                          {request.status === 'PENDING' && (
                            <div className="flex shrink-0 items-center gap-2">
                              <Button
                                disabled={respondLoading}
                                onClick={() => handleRejectClick(request.id, request.profile.name)}
                                size="sm"
                                type="button"
                                variant="outline"
                              >
                                {respondLoading ? <Spinner className="size-4" /> : <XIcon className="size-4" />}
                                {t('Reject')}
                              </Button>
                              <Button
                                disabled={respondLoading}
                                onClick={() => handleResponse(request.id, true)}
                                size="sm"
                                type="button"
                              >
                                {respondLoading ? <Spinner className="size-4" /> : <CheckIcon className="size-4" />}
                                {t('Approve')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No join requests found')}</EmptyTitle>
                  {watchName && <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>}
                </EmptyHeader>
              </Empty>
            )}
          </div>
        </Form>
      </div>

      {/* Reject Confirmation Modal */}
      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Reject')}
        loading={respondLoading}
        onConfirm={confirmRejectRequest}
        onOpenChange={(e) => {
          setRejectConfirmModal(e.open)
          if (!e.open) {
            setRequestToReject(null)
          }
        }}
        open={rejectConfirmModal}
        text={t('Are you sure you want to reject the join request from {{name}}?', {
          name: requestToReject?.profileName,
        })}
        title={t('Reject Join Request')}
        variant="delete"
      />
    </div>
  )
}
